import * as DM from '@radix-ui/react-dropdown-menu';
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { cn } from './cn';
import { Icon } from './icon';

/**
 * Dropdown menu: opens/closes with its button, closes on outside click and Escape, keeps
 * `aria-expanded` in sync (Radix) — FR-NAV-005. Used by the AppShell menus and row actions.
 */
export function Menu({
  trigger,
  label,
  align = 'end',
  children,
}: {
  trigger: ReactNode;
  label: string;
  align?: 'start' | 'end';
  children: ReactNode;
}) {
  return (
    <DM.Root modal={false}>
      <DM.Trigger asChild aria-label={label}>
        {trigger}
      </DM.Trigger>
      <DM.Portal>
        <DM.Content
          align={align}
          sideOffset={6}
          aria-label={label}
          className="z-50 min-w-56 rounded-xl border border-border bg-surface p-1.5 shadow-lg"
        >
          {children}
        </DM.Content>
      </DM.Portal>
    </DM.Root>
  );
}

const itemClass = (active?: boolean) =>
  cn(
    'flex cursor-pointer items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm outline-none',
    'data-[highlighted]:bg-canvas',
    active ? 'bg-brand-50 font-medium text-brand-700' : 'text-ink',
  );

export function MenuLink({
  to,
  icon,
  active,
  children,
}: {
  to: string;
  icon?: string;
  active?: boolean;
  children: ReactNode;
}) {
  return (
    <DM.Item asChild>
      <Link to={to} className={itemClass(active)} aria-current={active ? 'page' : undefined}>
        {icon && <Icon name={icon} className="size-4 text-ink-muted" />}
        {children}
      </Link>
    </DM.Item>
  );
}

export function MenuButton({
  icon,
  onSelect,
  active,
  children,
}: {
  icon?: string;
  onSelect: () => void;
  active?: boolean;
  children: ReactNode;
}) {
  return (
    <DM.Item className={itemClass(active)} onSelect={onSelect}>
      {icon && <Icon name={icon} className="size-4 text-ink-muted" />}
      {children}
    </DM.Item>
  );
}

export function MenuLabel({ children }: { children: ReactNode }) {
  return (
    <DM.Label className="px-2.5 pb-1 pt-2 text-xs font-medium uppercase tracking-wide text-ink-muted">
      {children}
    </DM.Label>
  );
}

export function MenuSeparator() {
  return <DM.Separator className="my-1.5 h-px bg-border" />;
}
